/** Group parent codes (no direct billing — used for UI grouping only). */
export const FEE_CATEGORY_GROUP_PARENT_CODES = new Set(['XE', 'THE', 'SAC', 'DVPCT', 'DV002', 'KTDV']);

export const FEE_GROUP_PARENT_DISPLAY_NAMES: Record<string, string> = {
  XE: 'Phí gửi xe',
  THE: 'Phí phát hành thẻ',
  SAC: 'Phí sạc xe điện',
  DVPCT: 'Phí cấp thẻ',
  DV002: 'Phí gửi xe',
  KTDV: 'Thu khai thác dịch vụ',
};

export function isFeeCategoryGroupParent(feeCode?: string | null): boolean {
  const code = (feeCode ?? '').trim().toUpperCase();
  return FEE_CATEGORY_GROUP_PARENT_CODES.has(code);
}

export function isKtdvContractFeeCode(feeCode?: string | null): boolean {
  const code = (feeCode ?? '').trim().toUpperCase();
  return code.startsWith('T-KTDV');
}

export function isKtdvFeeCategoryCode(feeCode?: string | null): boolean {
  const code = (feeCode ?? '').trim().toUpperCase();
  return code === 'KTDV' || isKtdvContractFeeCode(code);
}

export const RESIDENTIAL_MANAGEMENT_FEE_CODE = 'DV001';
export const TM_MANAGEMENT_FEE_CODE = 'DV008';

export type UsageTypeManagementContext = {
  code?: string | null;
  managementFeeCode?: string | null;
};

function normalizeFeeCode(feeCode?: string | null): string {
  return (feeCode ?? '').trim().toUpperCase();
}

export function resolveManagementFeeCodeForUsageType(
  usageType?: UsageTypeManagementContext | string | null,
): string | null {
  const ctx: UsageTypeManagementContext =
    typeof usageType === 'string' ? { code: usageType } : usageType ?? {};
  const configured = normalizeFeeCode(ctx.managementFeeCode);
  if (configured) return configured;
  const usage = (ctx.code ?? '').trim().toUpperCase();
  if (usage === 'TM') return TM_MANAGEMENT_FEE_CODE;
  if (usage === 'CAN_HO') return RESIDENTIAL_MANAGEMENT_FEE_CODE;
  return null;
}

export function collectConfiguredManagementFeeCodes(
  usageTypes: UsageTypeManagementContext[],
): Set<string> {
  const codes = new Set<string>();
  for (const usageType of usageTypes) {
    const configured = normalizeFeeCode(usageType.managementFeeCode);
    if (configured) {
      codes.add(configured);
    }
    const resolved = resolveManagementFeeCodeForUsageType(usageType);
    if (resolved) codes.add(resolved);
  }
  return codes;
}

function toManagementFeeCodeSet(
  buildingManagementFeeCodes?: ReadonlySet<string> | readonly string[] | null,
): ReadonlySet<string> | undefined {
  if (!buildingManagementFeeCodes) {
    return undefined;
  }
  if (buildingManagementFeeCodes instanceof Set) {
    return buildingManagementFeeCodes;
  }
  const iterable: string[] = buildingManagementFeeCodes instanceof Set
    ? Array.from(buildingManagementFeeCodes)
    : Array.from(buildingManagementFeeCodes);
  const codes: string[] = [];
  for (const row of iterable) {
    const normalized = normalizeFeeCode(row);
    if (normalized) {
      codes.push(normalized);
    }
  }
  return new Set(codes);
}

export function isAssignableManagementFeeCode(
  feeCode?: string | null,
  buildingManagementFeeCodes?: ReadonlySet<string> | readonly string[] | null,
): boolean {
  const code = normalizeFeeCode(feeCode);
  if (!code) return false;
  const configured = toManagementFeeCodeSet(buildingManagementFeeCodes);
  if (configured && configured.size > 0) {
    return configured.has(code);
  }
  return code === RESIDENTIAL_MANAGEMENT_FEE_CODE || code === TM_MANAGEMENT_FEE_CODE;
}

/** @deprecated Prefer isAssignableManagementFeeCode with building codes. */
export function isManagementFeeCode(
  feeCode?: string | null,
  buildingManagementFeeCodes?: ReadonlySet<string> | readonly string[] | null,
): boolean {
  return isAssignableManagementFeeCode(feeCode, buildingManagementFeeCodes);
}

export function apartmentMatchesManagementFeeCategory(
  usageType?: UsageTypeManagementContext | string | null,
  feeCode?: string | null,
  buildingManagementFeeCodes?: ReadonlySet<string> | readonly string[] | null,
): boolean {
  if (!isAssignableManagementFeeCode(feeCode, buildingManagementFeeCodes)) return true;
  const expected = resolveManagementFeeCodeForUsageType(usageType);
  if (!expected) return false;
  return normalizeFeeCode(feeCode) === expected;
}

export function isCasualParkingFeeCode(feeCode?: string | null): boolean {
  return (feeCode ?? '').trim().toUpperCase() === 'T-VL001';
}

/** Fee categories eligible for bulk create in Quản lý phí dịch vụ. */
export function isBulkCreatableFeeCode(feeCode?: string | null): boolean {
  if (!feeCode) return false;
  const code = feeCode.trim().toUpperCase();
  if (isFeeCategoryGroupParent(code) || isCasualParkingFeeCode(code)) {
    return false;
  }
  // Card issuance is manual-only — created per card, not bulk per apartment.
  if (code.startsWith('THE') || code.startsWith('DVPCT')) {
    return false;
  }
  return (
    code.startsWith('DV') ||
    code.startsWith('XE') ||
    code.startsWith('SAC') ||
    isKtdvContractFeeCode(code)
  );
}

/** Handover migration fee — opening debt from legacy receivable file. */
export const HANDOVER_FEE_CODE = 'CNNBG';

export function isHandoverFeeCode(feeCode?: string | null): boolean {
  return (feeCode ?? '').trim().toUpperCase() === HANDOVER_FEE_CODE;
}

type PaymentFeeGroupSample = {
  feeCode?: string | null;
  invoiceType?: string | null;
};

function paymentFeeGroupSortRank(fees: PaymentFeeGroupSample[]): number {
  const sample = fees[0];
  if (!sample) return 50;
  if (sample.invoiceType === 'CASUAL_PARKING' || isCasualParkingFeeCode(sample.feeCode)) {
    return 0;
  }
  if (sample.invoiceType === 'OTHER') {
    return 1;
  }
  if (isHandoverFeeCode(sample.feeCode)) {
    return 2;
  }
  if (isManagementFeeCode(sample.feeCode)) {
    return 3;
  }
  return 50;
}

export function comparePaymentFeeGroupOrder(
  a: { fees: PaymentFeeGroupSample[] },
  b: { fees: PaymentFeeGroupSample[] },
): number {
  const rankDiff = paymentFeeGroupSortRank(a.fees) - paymentFeeGroupSortRank(b.fees);
  if (rankDiff !== 0) {
    return rankDiff;
  }
  const aCode = a.fees[0]?.feeCode;
  const bCode = b.fees[0]?.feeCode;
  return (aCode || '').localeCompare(bCode || '');
}

export function compareFeeCategoryDisplayOrder(
  a: { feeCategory: { feeCode?: string } },
  b: { feeCategory: { feeCode?: string } },
): number {
  const aHandover = isHandoverFeeCode(a.feeCategory.feeCode);
  const bHandover = isHandoverFeeCode(b.feeCategory.feeCode);
  if (aHandover && !bHandover) return -1;
  if (!aHandover && bHandover) return 1;
  return (a.feeCategory.feeCode || '').localeCompare(b.feeCategory.feeCode || '');
}

export function isResidentFeeCode(feeCode?: string | null): boolean {
  if (!feeCode) return false;
  const code = feeCode.trim().toUpperCase();
  return (
    isHandoverFeeCode(code) ||
    code.startsWith('DV') ||
    code.startsWith('XE') ||
    code.startsWith('THE') ||
    code.startsWith('SAC') ||
    code.startsWith('DVPCT') ||
    code.startsWith('T-KTDV') ||
    code === 'KTDV' ||
    code === 'T-VL001'
  );
}

/** Legacy child codes or XE001 / THE001 / SAC001 — parent grouping for display only. */
export function getParentFeeCode(feeCode: string): string | null {
  const normalized = feeCode.trim().toUpperCase();
  if (/^XE\d{3}$/.test(normalized)) {
    return 'XE';
  }
  if (/^THE\d{3}$/.test(normalized)) {
    return 'THE';
  }
  if (/^SAC\d{3}$/.test(normalized)) {
    return 'SAC';
  }
  if (/^T-KTDV\d{2}$/.test(normalized)) {
    return 'KTDV';
  }
  if (/^DVPCT[A-Z]$/.test(normalized)) {
    return 'DVPCT';
  }
  const match = normalized.match(/^DV(\d+)([A-Z]+)$/);
  return match ? `DV${match[1]}` : null;
}

export interface ServiceFeeDisplayGroup<T> {
  key: string;
  headerTitle: string | null;
  items: T[];
}

type FeeCategoryLike = {
  feeCategoryId: string;
  feeCategory: {
    feeCode?: string;
    name: string;
    isActive?: boolean;
  };
};

export function isActiveFeeCategory(item: FeeCategoryLike): boolean {
  return item.feeCategory.isActive !== false;
}

/** Thu khai thác dịch vụ — chỉ căn hộ loại sử dụng TM (Thương mại). */
export function apartmentShowsKtdvServiceFees(usageTypeCode?: string | null): boolean {
  return (usageTypeCode ?? '').trim().toUpperCase() === 'TM';
}

export function filterKtdvFeeCategoriesForApartmentUsage<T extends FeeCategoryLike>(
  categories: T[],
  usageTypeCode?: string | null,
): T[] {
  if (apartmentShowsKtdvServiceFees(usageTypeCode)) {
    return categories;
  }
  return categories.filter((item) => !isKtdvFeeCategoryCode(item.feeCategory.feeCode));
}

export function filterManagementFeeCategoriesForApartmentUsage<T extends FeeCategoryLike>(
  categories: T[],
  usageType?: UsageTypeManagementContext | string | null,
  buildingManagementFeeCodes?: ReadonlySet<string> | readonly string[] | null,
): T[] {
  return categories.filter((item) =>
    apartmentMatchesManagementFeeCategory(
      usageType,
      item.feeCategory.feeCode,
      buildingManagementFeeCodes,
    ),
  );
}

export function filterResidentFeeCategories<T extends FeeCategoryLike>(categories: T[]): T[] {
  return categories
    .filter(
      (item) => isResidentFeeCode(item.feeCategory.feeCode) && isActiveFeeCategory(item),
    )
    .sort(compareFeeCategoryDisplayOrder);
}

/** Apartment Danh mục phí tab — active resident fee codes only. */
export function filterApartmentDanhMucPhiCategories<T extends FeeCategoryLike>(categories: T[]): T[] {
  return categories
    .filter(
      (item) => isResidentFeeCode(item.feeCategory.feeCode) && isActiveFeeCategory(item),
    )
    .sort(compareFeeCategoryDisplayOrder);
}

export function getPaymentFeeGroupKey(fee: {
  feeCategoryId?: string | null;
  feeCode?: string | null;
  categoryName?: string | null;
  isParkingFee?: boolean;
}): string {
  const code = (fee.feeCode ?? '').trim().toUpperCase();

  // Linked invoices: one selectable group per fee category (XE001, XE006, …).
  if (fee.feeCategoryId) {
    return fee.feeCategoryId;
  }

  if (code) {
    const parent = getParentFeeCode(code);
    if (parent) return `group:${parent}`;
    if (isFeeCategoryGroupParent(code)) return `group:${code}`;
    return `code:${code}`;
  }
  const name = (fee.categoryName ?? '').toLowerCase();
  if (fee.isParkingFee || name.includes('gửi xe') || name.includes('gui xe')) {
    return 'group:XE';
  }
  if (name.includes('phí điện') || name.includes('phi dien') || name === 'phí điện') {
    return 'group:utility:electric';
  }
  if (name.includes('phí nước') || name.includes('phi nuoc') || name === 'phí nước') {
    return 'group:utility:water';
  }
  if (name.includes('phát hành thẻ') || name.includes('phat hanh the') || name.includes('cấp thẻ')) {
    return 'group:THE';
  }
  if (name.includes('sạc') || name.includes('sac')) {
    return 'group:SAC';
  }
  return fee.feeCategoryId || fee.categoryName || 'other';
}

export function getPaymentFeeGroupLabel(fee: {
  feeCode?: string | null;
  categoryName?: string | null;
}): string {
  const name = (fee.categoryName ?? '').trim();
  if (name) {
    return name;
  }

  const code = (fee.feeCode ?? '').trim().toUpperCase();
  if (code) {
    const parent = getParentFeeCode(code);
    if (parent && FEE_GROUP_PARENT_DISPLAY_NAMES[parent]) {
      return FEE_GROUP_PARENT_DISPLAY_NAMES[parent];
    }
    if (isFeeCategoryGroupParent(code)) {
      return FEE_GROUP_PARENT_DISPLAY_NAMES[code] ?? 'Phí khác';
    }
  }

  return 'Phí khác';
}

export function buildServiceFeeDisplayGroups<T extends FeeCategoryLike>(
  activeCategories: T[],
  allCategories: T[],
  getTitle: (item: T) => string,
): ServiceFeeDisplayGroup<T>[] {
  const filtered = activeCategories.filter(
    (item) => isResidentFeeCode(item.feeCategory.feeCode) && isActiveFeeCategory(item),
  );

  const allByCode = new Map<string, T>();
  [...allCategories, ...activeCategories].forEach((item) => {
    const code = item.feeCategory.feeCode?.trim().toUpperCase();
    if (code && !allByCode.has(code)) {
      allByCode.set(code, item);
    }
  });

  const childrenByParent = new Map<string, T[]>();
  const parentCodesWithChildren = new Set<string>();

  filtered.forEach((item) => {
    const code = item.feeCategory.feeCode!.trim().toUpperCase();
    const parentCode = getParentFeeCode(code);
    if (!parentCode) {
      return;
    }

    parentCodesWithChildren.add(parentCode);
    const siblings = childrenByParent.get(parentCode) ?? [];
    siblings.push(item);
    childrenByParent.set(parentCode, siblings);
  });

  childrenByParent.forEach((children) => {
    children.sort((a, b) =>
      (a.feeCategory.feeCode || '').localeCompare(b.feeCategory.feeCode || ''),
    );
  });

  const groups: ServiceFeeDisplayGroup<T>[] = [];
  const processedIds = new Set<string>();

  Array.from(parentCodesWithChildren)
    .sort()
    .forEach((parentCode) => {
      const children = childrenByParent.get(parentCode) ?? [];
      if (children.length === 0) {
        return;
      }

      const parentItem = allByCode.get(parentCode);
      const headerTitle = parentItem
        ? getTitle(parentItem)
        : FEE_GROUP_PARENT_DISPLAY_NAMES[parentCode] ?? parentCode;

      groups.push({
        key: `group-${parentCode}`,
        headerTitle,
        items: children,
      });

      children.forEach((child) => processedIds.add(child.feeCategoryId));
      if (parentItem) {
        processedIds.add(parentItem.feeCategoryId);
      }
    });

  filtered.forEach((item) => {
    if (processedIds.has(item.feeCategoryId)) {
      return;
    }

    groups.push({
      key: item.feeCategoryId,
      headerTitle: null,
      items: [item],
    });
  });

  return groups.sort((a, b) => {
    const codeA = a.items[0]?.feeCategory.feeCode ?? '';
    const codeB = b.items[0]?.feeCategory.feeCode ?? '';
    if (isHandoverFeeCode(codeA) && !isHandoverFeeCode(codeB)) return -1;
    if (!isHandoverFeeCode(codeA) && isHandoverFeeCode(codeB)) return 1;
    return codeA.localeCompare(codeB);
  });
}

/** Flat table order: parent row followed by child rows (Danh mục phí list). */
export function buildFeeCategoryTableRows<T extends { id: string; feeCode?: string | null }>(
  categories: T[],
): Array<{ item: T; isChild: boolean }> {
  const sorted = [...categories].sort((a, b) =>
    (a.feeCode || '').localeCompare(b.feeCode || ''),
  );
  const byCode = new Map<string, T>();
  sorted.forEach((item) => {
    const code = item.feeCode?.trim().toUpperCase();
    if (code) {
      byCode.set(code, item);
    }
  });

  const childrenByParent = new Map<string, T[]>();
  sorted.forEach((item) => {
    const code = item.feeCode?.trim().toUpperCase() || '';
    const parentCode = getParentFeeCode(code);
    if (!parentCode) {
      return;
    }
    const siblings = childrenByParent.get(parentCode) ?? [];
    siblings.push(item);
    childrenByParent.set(parentCode, siblings);
  });

  const rows: Array<{ item: T; isChild: boolean }> = [];
  const processed = new Set<string>();

  Array.from(childrenByParent.keys())
    .sort()
    .forEach((parentCode) => {
      const parent = byCode.get(parentCode);
      if (parent) {
        rows.push({ item: parent, isChild: false });
        processed.add(parent.id);
      }
      const children = childrenByParent.get(parentCode) ?? [];
      children.forEach((child) => {
        rows.push({ item: child, isChild: true });
        processed.add(child.id);
      });
    });

  sorted.forEach((item) => {
    if (!processed.has(item.id)) {
      rows.push({ item, isChild: false });
    }
  });

  return rows;
}

/** Grouped rows for apartment Danh mục phí tab (ApartmentFeeCategory shape). */
export function buildApartmentFeeCategoryTableRows<T extends FeeCategoryLike & { feeCategoryId: string }>(
  categories: T[],
): Array<{ item: T; isChild: boolean; isGroupParent: boolean }> {
  const sorted = [...categories].sort(compareFeeCategoryDisplayOrder);
  const byCode = new Map<string, T>();
  sorted.forEach((item) => {
    const code = item.feeCategory.feeCode?.trim().toUpperCase();
    if (code) {
      byCode.set(code, item);
    }
  });

  const childrenByParent = new Map<string, T[]>();
  sorted.forEach((item) => {
    const code = item.feeCategory.feeCode?.trim().toUpperCase() || '';
    if (isFeeCategoryGroupParent(code)) {
      return;
    }
    const parentCode = getParentFeeCode(code);
    if (!parentCode) {
      return;
    }
    const siblings = childrenByParent.get(parentCode) ?? [];
    siblings.push(item);
    childrenByParent.set(parentCode, siblings);
  });

  const rows: Array<{ item: T; isChild: boolean; isGroupParent: boolean }> = [];
  const processed = new Set<string>();
  const parentOrder = ['THE', 'XE', 'SAC', 'KTDV', 'DVPCT', 'DV002'];

  parentOrder.forEach((parentCode) => {
    const children = childrenByParent.get(parentCode);
    if (!children?.length) {
      return;
    }

    const parent = byCode.get(parentCode);
    if (parent) {
      rows.push({ item: parent, isChild: false, isGroupParent: true });
      processed.add(parent.feeCategoryId);
    }

    children
      .sort((a, b) => (a.feeCategory.feeCode || '').localeCompare(b.feeCategory.feeCode || ''))
      .forEach((child) => {
        rows.push({ item: child, isChild: true, isGroupParent: false });
        processed.add(child.feeCategoryId);
      });
  });

  sorted.forEach((item) => {
    if (!processed.has(item.feeCategoryId)) {
      rows.push({
        item,
        isChild: false,
        isGroupParent: isFeeCategoryGroupParent(item.feeCategory.feeCode),
      });
    }
  });

  return rows;
}

